import {
  z,
  ARABIC_LATIN_COMPANY_NAME_REGEX,
  ARABIC_LATIN_CITY_REGEX,
} from '@tasama/utils';
import type { CompanyTypeFormData } from '../../company-type/stores/company-type-store';

// Company type constants
export const COMPANY_TYPES = {
  INTERNATIONAL: 'INTERNATIONAL',
  KSA_PRIVATE: 'KSA_PRIVATE',
  PIF_OWNED: 'pif-owned',
} as const;

export const COMPANY_STATUS = {
  PIF_ESTABLISHED: 'PIF_ESTABLISHED',
} as const;

// Dropdown options
export const INDUSTRY_SECTORS = [
  {
    value: 'agriculture',
    label: 'Agriculture',
    labelKey: 'companyDetails.fields.industrySector.options.agriculture',
  },
  {
    value: 'consulting',
    label: 'Consulting',
    labelKey: 'companyDetails.fields.industrySector.options.consulting',
  },
  {
    value: 'construction',
    label: 'Construction',
    labelKey: 'companyDetails.fields.industrySector.options.construction',
  },
  {
    value: 'education',
    label: 'Education',
    labelKey: 'companyDetails.fields.industrySector.options.education',
  },
  {
    value: 'energy',
    label: 'Energy',
    labelKey: 'companyDetails.fields.industrySector.options.energy',
  },
  {
    value: 'entertainment',
    label: 'Entertainment',
    labelKey: 'companyDetails.fields.industrySector.options.entertainment',
  },
  {
    value: 'finance',
    label: 'Finance',
    labelKey: 'companyDetails.fields.industrySector.options.finance',
  },
  {
    value: 'healthcare',
    label: 'Healthcare',
    labelKey: 'companyDetails.fields.industrySector.options.healthcare',
  },
  {
    value: 'hospitality',
    label: 'Hospitality',
    labelKey: 'companyDetails.fields.industrySector.options.hospitality',
  },
  {
    value: 'manufacturing',
    label: 'Manufacturing',
    labelKey: 'companyDetails.fields.industrySector.options.manufacturing',
  },
  {
    value: 'real-estate',
    label: 'Real Estate',
    labelKey: 'companyDetails.fields.industrySector.options.realEstate',
  },
  {
    value: 'retail',
    label: 'Retail',
    labelKey: 'companyDetails.fields.industrySector.options.retail',
  },
  {
    value: 'technology',
    label: 'Technology',
    labelKey: 'companyDetails.fields.industrySector.options.technology',
  },
  {
    value: 'telecommunications',
    label: 'Telecommunications',
    labelKey: 'companyDetails.fields.industrySector.options.telecommunications',
  },
  {
    value: 'transportation',
    label: 'Transportation',
    labelKey: 'companyDetails.fields.industrySector.options.transportation',
  },
  {
    value: 'other',
    label: 'Other',
    labelKey: 'companyDetails.fields.industrySector.options.other',
  },
];

export const getIndustrySectorOptions = (t: (_key: string) => string) =>
  INDUSTRY_SECTORS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

export const EMPLOYEE_COUNT_VALUES = [
  '0-5',
  '6-25',
  '26-100',
  '101-200',
  '201-1000',
  '>1000',
] as const;
export const ANNUAL_REVENUE_VALUES = [
  '0-1M',
  '1M-5M',
  '5M-20M',
  '>20M',
] as const;
export const INVESTMENT_RAISED_VALUES = [
  '0-5M SAR',
  '5M-20M SAR',
  '20M-50M SAR',
  '50M-100M SAR',
  '>100M SAR',
] as const;
export const GCC_COUNTRY_CODES = ['SA', 'AE', 'KW', 'QA', 'BH', 'OM'] as const;

export const getEmployeeCountOptions = (t: (_key: string) => string) => [
  {
    value: '0-5',
    label: t('common:companyForm.employeeCountOptions.range_0_5'),
  },
  {
    value: '6-25',
    label: t('common:companyForm.employeeCountOptions.range_6_25'),
  },
  {
    value: '26-100',
    label: t('common:companyForm.employeeCountOptions.range_26_100'),
  },
  {
    value: '101-200',
    label: t('common:companyForm.employeeCountOptions.range_101_200'),
  },
  {
    value: '201-1000',
    label: t('common:companyForm.employeeCountOptions.range_201_1000'),
  },
  {
    value: '>1000',
    label: t('common:companyForm.employeeCountOptions.range_gt_1000'),
  },
];

export const getAnnualRevenueOptions = (t: (_key: string) => string) => [
  {
    value: '0-1M',
    label: t('common:companyForm.annualRevenueOptions.range_0_1M'),
  },
  {
    value: '1M-5M',
    label: t('common:companyForm.annualRevenueOptions.range_1M_5M'),
  },
  {
    value: '5M-20M',
    label: t('common:companyForm.annualRevenueOptions.range_5M_20M'),
  },
  {
    value: '>20M',
    label: t('common:companyForm.annualRevenueOptions.range_gt_20M'),
  },
];

export const getInvestmentRaisedOptions = (t: (_key: string) => string) => [
  {
    value: '0-5M SAR',
    label: t('common:companyForm.investmentRaisedOptions.range_0_5M'),
  },
  {
    value: '5M-20M SAR',
    label: t('common:companyForm.investmentRaisedOptions.range_5M_20M'),
  },
  {
    value: '20M-50M SAR',
    label: t('common:companyForm.investmentRaisedOptions.range_20M_50M'),
  },
  {
    value: '50M-100M SAR',
    label: t('common:companyForm.investmentRaisedOptions.range_50M_100M'),
  },
  {
    value: '>100M SAR',
    label: t('common:companyForm.investmentRaisedOptions.range_gt_100M'),
  },
];

// CHANGE: Removed COUNTRIES array as it's now fetched from API via countries service

// CHANGE: Updated GCC_COUNTRIES to use country codes as values instead of names
export const getGccCountries = (t: (_key: string) => string) => [
  { value: 'SA', label: t('companyDetails.gccCountries.SA') },
  { value: 'AE', label: t('companyDetails.gccCountries.AE') },
  { value: 'KW', label: t('companyDetails.gccCountries.KW') },
  { value: 'QA', label: t('companyDetails.gccCountries.QA') },
  { value: 'BH', label: t('companyDetails.gccCountries.BH') },
  { value: 'OM', label: t('companyDetails.gccCountries.OM') },
];

// Helper function to get field requirements based on company type
export const getFieldRequirements = (companyTypeData?: CompanyTypeFormData) => {
  const companyType = companyTypeData?.companyType;
  const companyStatus = companyTypeData?.companyStatus;

  // For INTERNATIONAL companies
  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    return {
      companyName: { required: true },
      registrationNumber: { required: true },
      registrationStartDate: { required: true },
      website: { required: false },
      industrySector: { required: true },
      countryOfHeadquarter: { required: true },
      addressLine1: { required: true },
      city: { required: true },
      postcode: { required: false },
      numberOfEmployees: { required: true },
      annualRevenue: { required: true },
      totalInvestmentRaised: { required: true },
    };
  }

  // For KSA_PRIVATE companies
  if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
    return {
      companyName: { required: true },
      registrationNumber: { required: true },
      registrationStartDate: { required: true },
      website: { required: false },
      industrySector: { required: true },
      country: { required: true }, // Country of registration (disabled, prefilled with Saudi Arabia)
      addressLine1: { required: false },
      city: { required: false },
      postcode: { required: false },
      numberOfEmployees: { required: false },
      annualRevenue: { required: false },
    };
  }

  // For PIF_ESTABLISHED companies (existing behavior)
  if (
    companyType === COMPANY_TYPES.PIF_OWNED &&
    companyStatus === COMPANY_STATUS.PIF_ESTABLISHED
  ) {
    return {
      companyName: { required: true },
      registrationNumber: { required: true },
      registrationStartDate: { required: true },
      website: { required: false },
      industrySector: { required: true },
      country: { required: true }, // Country of registration (disabled, prefilled with Saudi Arabia)
      addressLine1: { required: false },
      city: { required: false },
      postcode: { required: false },
    };
  }

  // Default (PIF_ESTABLISHED behavior)
  return {
    companyName: { required: true },
    registrationNumber: { required: true },
    registrationStartDate: { required: true },
    website: { required: false },
    country: { required: true },
    addressLine1: { required: false },
    city: { required: false },
    postcode: { required: false },
  };
};

// Validation messages — resolved via i18n at call time
export const getValidationMessages = (t: (_key: string) => string) => ({
  companyName: {
    required: t('errors.validation.companyName.required'),
    max: t('errors.validation.companyName.max'),
    charset: t('errors.validation.companyName.charset'),
  },
  registrationNumber: {
    required: t('errors.validation.registrationNumber.required'),
    max: t('errors.validation.registrationNumber.max'),
    charset: t('errors.validation.registrationNumber.charset'),
    saudiFormat: t('errors.validation.registrationNumber.saudiFormat'),
    internationalFormat: t(
      'errors.validation.registrationNumber.internationalFormat'
    ),
  },
  registrationStartDate: {
    required: t('errors.validation.registrationDate.required'),
    format: t('errors.validation.registrationDate.format'),
    future: t('errors.validation.registrationDate.future'),
  },
  website: {
    invalid: t('errors.validation.website.invalid'),
  },
  country: {
    required: t('errors.validation.country.required'),
  },
  countryOfHeadquarter: {
    required: t('errors.validation.countryOfHeadquarter.required'),
  },
  city: {
    charset: t('errors.validation.city.charset'),
    required: t('errors.validation.city.required'),
  },
  address: {
    required: t('errors.validation.address.required'),
  },
  postcode: {
    charset: t('errors.validation.postcode.charset'),
  },
  industrySector: {
    required: t('errors.validation.industrySector.required'),
  },
  numberOfEmployees: {
    required: t('errors.validation.numberOfEmployees.required'),
  },
  annualRevenue: {
    required: t('errors.validation.annualRevenue.required'),
  },
  totalInvestmentRaised: {
    required: t('errors.validation.totalInvestmentRaised.required'),
  },
});

// 2. Regex & helpers
const COMPANY_NAME_REGEX = ARABIC_LATIN_COMPANY_NAME_REGEX;
// CR Number regex patterns for different company types
// Saudi Companies (KSA_PRIVATE, PIF_ESTABLISHED): Exactly 10 digits
const CR_NUMBER_SAUDI_REGEX = /^[0-9]{10}$/;
// International Companies: Alphanumeric, hyphens, and spaces allowed
const CR_NUMBER_INTERNATIONAL_REGEX = /^[A-Za-z0-9-\s]+$/;
const CITY_REGEX = ARABIC_LATIN_CITY_REGEX;
const POSTCODE_REGEX = /^([A-Za-z0-9\s-]{3,12})?$/; // Made optional within regex

// Helper to parse DD/MM/YYYY and check if it's valid
const isValidDateString = (dateStr: string) => {
  // Accepts DD/MM/YYYY format (as provided by DateInput component)
  const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  // Also accepts YYYY-MM-DD or YYYY/MM/DD for backward compatibility
  const yyyymmddRegex = /^(\d{4})[/-](\d{2})[/-](\d{2})$/;

  let year: number, month: number, day: number;

  if (ddmmyyyyRegex.test(dateStr)) {
    // DD/MM/YYYY format
    const match = dateStr.match(ddmmyyyyRegex);
    if (!match) return false;

    day = Number(match[1]);
    month = Number(match[2]);
    year = Number(match[3]);
  } else if (yyyymmddRegex.test(dateStr)) {
    // YYYY-MM-DD or YYYY/MM/DD format
    const match = dateStr.match(yyyymmddRegex);
    if (!match) return false;

    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
  } else {
    return false;
  }

  const date = new Date(year, month - 1, day);

  // Validate logical date (e.g., prevents 31/02/2025)
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

// 4. Dynamic schema based on company type
export const getCompanyDetailsSchema = (
  companyTypeData?: CompanyTypeFormData,
  t?: (_key: string) => string
) => {
  const requirements = getFieldRequirements(companyTypeData);
  const companyType = companyTypeData?.companyType;
  const companyStatus = companyTypeData?.companyStatus;

  const messages = getValidationMessages(t ?? ((key: string) => key));

  // Helper function to create crNumber validation based on company type
  const getCrNumberValidation = () => {
    // For Saudi companies (KSA_PRIVATE and PIF_ESTABLISHED): Exactly 10 digits
    if (
      companyType === COMPANY_TYPES.KSA_PRIVATE ||
      (companyType === COMPANY_TYPES.PIF_OWNED &&
        companyStatus === COMPANY_STATUS.PIF_ESTABLISHED)
    ) {
      return z
        .string()
        .min(1, messages.registrationNumber.required)
        .transform((val) => val.replace(/\s/g, '')) // Strip spaces before validation
        .refine(
          (val) => CR_NUMBER_SAUDI_REGEX.test(val),
          messages.registrationNumber.saudiFormat
        );
    }

    // For International companies: Alphanumeric, hyphens, and spaces (max 50 chars)
    if (companyType === COMPANY_TYPES.INTERNATIONAL) {
      return z
        .string()
        .min(1, messages.registrationNumber.required)
        .max(50, messages.registrationNumber.internationalFormat)
        .regex(
          CR_NUMBER_INTERNATIONAL_REGEX,
          messages.registrationNumber.internationalFormat
        );
    }

    // For PIF_UNDER_DEVELOPMENT: Optional field, no format enforcement
    // (This is the default case when company type doesn't match above)
    return z.string().optional().or(z.literal(''));
  };

  const baseSchema = {
    // COMPANY NAME: Always required
    companyName: z
      .string()
      .trim()
      .min(1, messages.companyName.required)
      .max(200, messages.companyName.max)
      .regex(COMPANY_NAME_REGEX, messages.companyName.charset),

    // REGISTRATION NUMBER: Always required
    registrationNumber: getCrNumberValidation(),

    // REGISTRATION DATE: Always required
    registrationStartDate: z
      .string()
      .trim()
      .min(1, messages.registrationStartDate.required)
      .refine(
        (val) => isValidDateString(val),
        messages.registrationStartDate.format
      )
      .refine(
        (val) => {
          let year: number, month: number, day: number;

          // Handle DD/MM/YYYY format
          const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
          if (ddmmyyyyRegex.test(val)) {
            const match = val.match(ddmmyyyyRegex);
            if (!match) return false;
            day = Number(match[1]);
            month = Number(match[2]);
            year = Number(match[3]);
          } else {
            // Handle YYYY-MM-DD or YYYY/MM/DD format
            const parts = val.split(/[-/]/).map(Number);
            if (parts.length !== 3) return false;
            const [y, m, d] = parts;
            year = y!;
            month = m!;
            day = d!;
          }

          const inputDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return inputDate <= today;
        },
        { error: messages.registrationStartDate.future }
      ),

    // WEBSITE: Always optional
    website: z
      .string()
      .trim()
      .refine((val) => {
        if (val === '') return true;
        // Regex: Optional protocol (http:// or https://), followed by valid domain
        // Ensures no multiple slashes after protocol
        const websiteRegex =
          /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?(\/[^\s]*)?$/;
        return websiteRegex.test(val);
      }, messages.website.invalid),
  };

  // Add conditional fields based on company type
  const conditionalFields: Record<string, z.ZodTypeAny> = {};

  // Industry Sector - required for INTERNATIONAL and KSA_PRIVATE
  if (requirements.industrySector) {
    conditionalFields.industrySector = requirements.industrySector.required
      ? z.preprocess(
          (val) => (val === undefined || val === null ? '' : val),
          z.string().min(1, messages.industrySector.required)
        )
      : z.string().optional().or(z.literal(''));
  }

  // Country fields
  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    // For international companies, use "Country of headquarters"
    conditionalFields.countryOfHeadquarter = z.preprocess(
      (val) => (val === undefined || val === null ? '' : val),
      z.string().min(1, messages.countryOfHeadquarter.required)
    );
  } else {
    // For KSA companies, use "Country of registration" (disabled, prefilled)
    conditionalFields.country = z.string().min(1, messages.country.required);
  }

  // Address fields
  if (requirements.addressLine1) {
    conditionalFields.addressLine1 = requirements.addressLine1.required
      ? z.string().trim().min(1, messages.address.required)
      : z.string().trim();
  }

  if (requirements.city) {
    conditionalFields.city = requirements.city.required
      ? z
          .string()
          .trim()
          .min(1, messages.city.required)
          .regex(CITY_REGEX, messages.city.charset)
      : z
          .string()
          .trim()
          .regex(CITY_REGEX, messages.city.charset)
          .optional()
          .or(z.literal(''));
  }

  if (requirements.postcode !== undefined) {
    conditionalFields.postcode = z
      .string()
      .trim()
      .regex(POSTCODE_REGEX, messages.postcode.charset)
      .optional()
      .or(z.literal(''));
  }

  // Additional fields for INTERNATIONAL companies
  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    conditionalFields.numberOfEmployees = z
      .string()
      .min(1, messages.numberOfEmployees.required);
    conditionalFields.annualRevenue = z
      .string()
      .min(1, messages.annualRevenue.required);
    conditionalFields.totalInvestmentRaised = z
      .string()
      .min(1, messages.totalInvestmentRaised.required);
  }

  // Additional optional fields for KSA_PRIVATE companies
  if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
    conditionalFields.numberOfEmployees = z
      .string()
      .optional()
      .or(z.literal(''));
    conditionalFields.annualRevenue = z.string().optional().or(z.literal(''));
  }

  return z.object({ ...baseSchema, ...conditionalFields });
};

// Type-inference anchor — not used for runtime validation; the page always calls getCompanyDetailsSchema(companyTypeData, t)
export const companyDetailsSchema = getCompanyDetailsSchema(
  undefined,
  (key: string) => key
);

// Extended type to include all possible fields across company types
export interface ExtendedCompanyDetailsFormValues {
  companyName: string;
  registrationNumber: string;
  registrationStartDate: string;
  website?: string;
  industrySector?: string;
  country?: string; // For KSA companies (country of registration)
  countryOfHeadquarter?: string; // For international companies
  addressLine1?: string;
  city?: string;
  postcode?: string;
  numberOfEmployees?: string;
  annualRevenue?: string;
  totalInvestmentRaised?: string;
}

export type CompanyDetailsFormValues = z.infer<typeof companyDetailsSchema>;

// Type export for the field validation function
export type CompanyDetailsFieldName = keyof ExtendedCompanyDetailsFormValues;

// Dynamic default values based on company type
export const getCompanyDetailsDefaultValues = (
  companyTypeData?: CompanyTypeFormData
): Partial<ExtendedCompanyDetailsFormValues> => {
  const companyType = companyTypeData?.companyType;

  const baseDefaults = {
    companyName: '',
    registrationNumber: '',
    registrationStartDate: '',
    website: '',
    addressLine1: '',
    city: '',
    postcode: '',
  };

  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    return {
      ...baseDefaults,
      industrySector: '',
      countryOfHeadquarter: '',
      numberOfEmployees: '',
      annualRevenue: '',
      totalInvestmentRaised: '',
    };
  }

  if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
    return {
      ...baseDefaults,
      industrySector: '',
      // CHANGE: Updated to use country code instead of name
      country: 'SA',

      numberOfEmployees: '',
      annualRevenue: '',
    };
  }

  // Default for PIF_ESTABLISHED and others
  return {
    ...baseDefaults,
    // CHANGE: Updated to use country code instead of name
    country: 'SA',
  };
};

// CHANGE: Updated to use country code instead of name
export const companyDetailsDefaultValues: Partial<ExtendedCompanyDetailsFormValues> =
  {
    companyName: '',
    registrationNumber: '',
    registrationStartDate: '',
    website: '',
    country: 'SA',
    addressLine1: '',
    city: '',
    postcode: '',
  };

// Field validation helper for individual field validation
export const validateCompanyDetailsField = (
  field: keyof ExtendedCompanyDetailsFormValues,
  value: unknown,
  companyTypeData?: CompanyTypeFormData
): string | null => {
  try {
    // Create a test object with just this field
    const testData = { [field]: value };
    // Get the appropriate schema based on company type
    const schema = getCompanyDetailsSchema(companyTypeData);
    // Pick only the field schema from the main schema
    const fieldSchema = schema.pick({ [field]: true } as Record<
      keyof ExtendedCompanyDetailsFormValues,
      true
    >);
    fieldSchema.parse(testData);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldError = error.issues.find((issue) =>
        issue.path.includes(field)
      );
      if (fieldError) {
        return fieldError.message;
      }
    }
    return 'Validation failed';
  }
};
